import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationType } from '@guryou/core';
import { Block, GoogleMap, GoogleMapsAutocomplete, HiddenField } from '@guryou/html-components';
import { sizes } from '@guryou/html-theme';
import { parseGoogleMapLocation, parseGoogleMapPlace } from '@guryou/utils';
import { useOnboardContext } from 'onboard/context';
import { dealTokens, SystemCheckbox, SystemFormGrid, SystemTextArea, SystemTextInput } from 'components/shared';

import SelectableLocationGroup from './LocationCube';

const hasAddress = values => {
  return values?.address && values?.latitude && values?.longitude;
};

const LocationPicker = ({ showCheckBox, checkBoxLabel, location, disabled = false }) => {
  const { t } = useTranslation();
  const mapsAutocomplete = useRef();
  const defaultValue = location?.address
    ? LocationType.mapLocation
    : location?.note || location?.url
    ? LocationType.plainNote
    : LocationType.providerLocation;

  const { location: providerLocation } = useOnboardContext();
  const [locationObject, setLocationObject] = useState(location || {});
  const [selectedView, setSelectedView] = useState(defaultValue);
  const [propagateLocation, setPropagateLocation] = useState(false);

  // const providerDetails = useSelector(state => state.provider.generalInfo);

  const handleAutocompleteChange = e => {
    if (!e?.target?.value) {
      setLocationObject({});
    }
  };

  const handleAutocompleteOnChange = e => {
    if (!e?.target?.value) {
      setLocationObject({});
    }
    const val = e?.target?.value;
    setLocationObject(prev => ({ ...prev, address: val }));
  };

  return (
    <Block margin={0}>
      <SelectableLocationGroup onChange={val => setSelectedView(val)} value={defaultValue} />

      {selectedView === LocationType.plainNote && (
        <SystemFormGrid singleColumn>
          <SystemTextInput
            disabled={disabled}
            label="URL"
            name="url"
            value={locationObject.url || locationObject.locationNote || locationObject.note || ''}
          />
          <SystemTextArea
            disabled={disabled}
            rows={4}
            label={t('GENERAL.leaveNote')}
            placeholder={t('GENERAL.enterValidUrl')}
            name="locationNote"
            value={locationObject.locationNote || locationObject.note || ''}
          />
        </SystemFormGrid>
      )}
      {selectedView === LocationType.mapLocation && (
        <>
          <HiddenField name="latitude" value={locationObject.latitude || ''} />
          <HiddenField name="longitude" value={locationObject.longitude || ''} />
          <HiddenField name="address" value={locationObject.address || ''} />
          <GoogleMapsAutocomplete
            disabled={disabled}
            fullWidth
            margin="none"
            inputRef={mapsAutocomplete}
            value={locationObject?.address || ''}
            onBlur={handleAutocompleteChange}
            onChange={handleAutocompleteOnChange}
            placeholder={t('GENERAL.enterLocation')}
            required
          />
          <div
            style={{
              marginTop: dealTokens.space[4],
              borderRadius: dealTokens.radius.md,
              overflow: 'hidden',
              border: `1px solid ${dealTokens.color.border}`,
            }}
          >
            <GoogleMap
              style={{ height: '25vh', minHeight: `${sizes.small}px`, width: `100%` }}
              defaultCenter={{ lat: locationObject.latitude ?? 41.9028, lng: locationObject.longitude ?? 12.4964 }}
              autocompleteRef={mapsAutocomplete}
              isMarkerShown={!!locationObject.latitude}
              onMapClick={location => setLocationObject(parseGoogleMapLocation(location))}
              onPlaceSelected={place => setLocationObject(parseGoogleMapPlace(place))}
            />
          </div>
        </>
      )}

      {selectedView === LocationType.providerLocation && hasAddress(providerLocation) && (
        <>
          <GoogleMapsAutocomplete
            disabled={true}
            fullWidth
            margin="none"
            inputRef={mapsAutocomplete}
            value={providerLocation?.address || ''}
            placeholder={t('GENERAL.enterLocation')}
            required
          />
          <div
            style={{
              marginTop: dealTokens.space[4],
              borderRadius: dealTokens.radius.md,
              overflow: 'hidden',
              border: `1px solid ${dealTokens.color.border}`,
            }}
          >
            <GoogleMap
              style={{ height: '25vh', minHeight: `${sizes.small}px`, width: `100%` }}
              defaultCenter={{ lat: providerLocation.latitude ?? 41.9028, lng: providerLocation.longitude ?? 12.4964 }}
              autocompleteRef={mapsAutocomplete}
              isMarkerShown={!!providerLocation.latitude}
              disabled={true}
            />
          </div>
        </>
      )}
      {/* SystemCheckbox is controlled and has no form field of its own, so the value reaches the
          Form through a HiddenField — rendered only while ticked, exactly like the legacy CheckBox
          it replaces (an unticked box was absent from the payload, which parseAsBool reads as false). */}
      {showCheckBox && (
        <>
          <SystemCheckbox label={checkBoxLabel} checked={propagateLocation} onChange={setPropagateLocation} />
          {propagateLocation && <HiddenField name="propagateLocation" value="true" />}
        </>
      )}
    </Block>
  );
};

export default LocationPicker;
